import { useEffect } from 'react';

const AGENT_URL = 'https://api.aiagentsmith.net';
const SCRIPT_ID = 'ai-agent-script';
const STYLE_ID = 'ai-agent-style';

const agentOptions = {
  agentId: '6a78f99a2f9cc90c5d416c8f',
  apiUrl: AGENT_URL,
  campaignId: '6a78fa6c2f9cc90c5d416d64',
  campaignUserId: '6a77484b2f9cc90c5d40510b',
  variables: {
    callee_name: 'VISITOR'
  },
  widgetConfig: {
    theme: 'dark',
    primaryColor: '#2563eb',
    placement: 'bottom-right',
    enableVoiceCall: true,
    enableTextChat: true,
    branding: {
      title: 'AutoReception AI',
      description: "I'm here to answer your questions. How can I help?",
      logo: null
    },
    listId: null,
    voiceSetting: {
      voiceProvider: 'deepgram',
      voiceGender: 'Female',
      voiceLanguage: 'English (US)',
      language_id: 'en-US',
      voiceId: '69a02bf21c06393e7225ab6a',
      voiceName: 'aura-2-andromeda-en'
    }
  }
};

let scriptPromise;

const addWidgetStyles = () => {
  if (document.getElementById(STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #agent-smith-widget-container {
      position: fixed !important;
      right: 20px !important;
      bottom: 20px !important;
      z-index: 2147483647 !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    #agent-smith-widget-container[hidden] {
      display: block !important;
    }

    #agent-smith-widget-container iframe,
    [id^="agent-smith-widget"] {
      z-index: 2147483647 !important;
    }

    @media (max-width: 640px) {
      #agent-smith-widget-container {
        right: 12px !important;
        bottom: 12px !important;
      }
    }
  `;

  document.head.appendChild(style);
};

const loadAgentScript = () => {
  if (window.loadAgent) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = `${AGENT_URL}/dist/style.css`;
    document.head.appendChild(stylesheet);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${AGENT_URL}/dist/agent.es.js`;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('The voice widget script could not be loaded.'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const keepWidgetVisible = () => {
  const container = document.querySelector('#agent-smith-widget-container');

  if (!container) {
    return;
  }

  container.hidden = false;
  container.style.setProperty('display', 'block', 'important');
  container.style.setProperty('visibility', 'visible', 'important');
  container.style.setProperty('opacity', '1', 'important');
  container.style.setProperty('z-index', '2147483647', 'important');
};

const initializeWidget = () => {
  if (!window.loadAgent || window.__autoReceptionWidgetInitialized) {
    keepWidgetVisible();
    return;
  }

  window.loadAgent(agentOptions);
  window.__autoReceptionWidgetInitialized = true;

  window.setTimeout(keepWidgetVisible, 300);
  window.setTimeout(keepWidgetVisible, 1200);
  window.setTimeout(keepWidgetVisible, 3000);
};

const AIWidget = () => {
  useEffect(() => {
    let observer;
    let retryTimer;

    addWidgetStyles();

    loadAgentScript()
      .then(() => {
        initializeWidget();

        observer = new MutationObserver(() => {
          keepWidgetVisible();

          if (!window.__autoReceptionWidgetInitialized && window.loadAgent) {
            initializeWidget();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'style', 'class']
        });

        retryTimer = window.setInterval(() => {
          if (window.loadAgent && !window.__autoReceptionWidgetInitialized) {
            initializeWidget();
          }

          keepWidgetVisible();
        }, 2000);
      })
      .catch((error) => {
        console.error('[AutoReception] Voice widget failed to load:', error);
      });

    return () => {
      observer?.disconnect();
      if (retryTimer) {
        window.clearInterval(retryTimer);
      }
    };
  }, []);

  return null;
};

export default AIWidget;